import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AppLayout } from '@/shared/components/Layout/AppLayout';
import { Spinner } from '@/shared/components/Feedback/Spinner';
import { ProtectedRoute } from '@/features/auth/components/ProtectedRoute';
import useAuthStore from '@/app/store/auth.store';
import useFeaturesStore from '@/app/store/features.store';

// Lazy load pages
const LoginPage = lazy(() => import('@/features/auth/pages/LoginPage'));
const PublicOnboardingPage = lazy(() => import('@/features/xroad/pages/PublicOnboardingPage'));
const VerifyEmailPage = lazy(() => import('@/features/xroad/pages/VerifyEmailPage'));
const DashboardPage = lazy(() => import('@/features/dashboard/pages/DashboardPage'));
const DatasourcesListPage = lazy(() => import('@/features/datasources/pages/DatasourcesListPage'));
const CreateDatasourcePage = lazy(() => import('@/features/datasources/pages/CreateDatasourcePage'));
const DatasourceDetailPage = lazy(() => import('@/features/datasources/pages/DatasourceDetailPage'));
const SchemaExplorerPageV2 = lazy(() => import('@/features/schema-explorer/pages/SchemaExplorerPageV2'));
const EndpointsPage = lazy(() => import('@/features/endpoints/pages/EndpointsPage'));
const CreateEndpointPage = lazy(() => import('@/features/endpoints/pages/CreateEndpointPage'));
const EndpointDetailPage = lazy(() => import('@/features/endpoints/pages/EndpointDetailPage'));
const QueryBuilderPage = lazy(() => import('@/features/query-builder/pages/QueryBuilderPage'));
const SyncPage = lazy(() => import('@/features/sync/pages/SyncPage'));
const SyncJobDetailPage = lazy(() => import('@/features/sync/pages/SyncJobDetailPage'));
const SyncTestPage = lazy(() => import('@/features/sync-test/pages/SyncTestPage'));
const ConsumerApplicationsPage = lazy(() => import('@/features/xroad/pages/ConsumerApplicationsPage'));
const SubscriptionsPage = lazy(() => import('@/features/xroad/pages/SubscriptionsPage'));
const PublishedServicesPage = lazy(() => import('@/features/xroad/pages/PublishedServicesPage'));
const PublishServiceWizard = lazy(() => import('@/features/xroad/components/PublishServiceWizard').then(m => ({ default: m.PublishServiceWizard })));
const CertificateMonitoringPage = lazy(() => import('@/features/xroad/pages/CertificateMonitoringPage'));
const ConsumeServicePage = lazy(() => import('@/features/xroad/pages').then(module => ({ default: module.ConsumeServicePage })));
const ServiceCatalogPage = lazy(() => import('@/features/xroad/pages/ServiceCatalogPage'));
const ApiKeysPage = lazy(() => import('@/features/xroad/pages/ApiKeysPage'));
const MyAccessPage = lazy(() => import('@/features/xroad/pages/MyAccessPage'));
const DataLensPage = lazy(() => import('@/features/datalens/pages/DataLensPage'));

// JSONata Pages
const JsonataPlaygroundPage = lazy(() => import('@/features/jsonata/pages/JsonataPlaygroundPage'));
const UDFManagerPage = lazy(() => import('@/features/jsonata/pages/UDFManagerPage'));

// DQ Pages
const DqDashboardPage = lazy(() => import('@/features/dq/pages/DqDashboardPage'));

// Intelligence Interopérabilité — SENUM rebrand of governance pages
const HubPage = lazy(() => import('@/features/governance/pages/HubPage'));
const GlossaryListPage = lazy(() => import('@/features/governance/pages/GlossaryListPage'));
const TermDetailPage = lazy(() => import('@/features/governance/pages/TermDetailPage'));
const QualityDashboardPage = lazy(() => import('@/features/governance/pages/QualityDashboardPage'));
const CatalogPage = lazy(() => import('@/features/governance/pages/CatalogPage'));
const LineageGraphPage = lazy(() => import('@/features/governance/pages/LineageGraphPage'));
const AccessSupervisionPage = lazy(() => import('@/features/governance/pages/AccessSupervisionPage'));

// Registry Builder Pages
const RegistryListPage = lazy(() => import('@/features/registry/pages/RegistryListPage'));
const CreateRegistryPage = lazy(() => import('@/features/registry/pages/CreateRegistryPage'));
const RegistryDetailPage = lazy(() => import('@/features/registry/pages/RegistryDetailPage'));

// Privacy Pages
const PrivacyRegistryPage = lazy(() => import('@/features/privacy/pages/PrivacyRegistryPage'));
const PrivacyCartographyPage = lazy(() => import('@/features/privacy/pages/PrivacyCartographyPage'));
const PrivacyCompliancePage = lazy(() => import('@/features/privacy/pages/PrivacyCompliancePage'));

// X-Road Portal Router (Multi-Tenant)
const XRoadModule = lazy(() => import('@/features/xroad/components/XRoadModule'));
const XRoadOverviewPage = lazy(() => import('@/features/xroad/pages/XRoadOverviewPage'));

// Tenant Portal Pages (Session 2B)
const OperatorConsolePage = lazy(() => import('@/features/tenant/pages/OperatorConsolePage'));
const ControlTowerPage = lazy(() => import('@/features/tenant/pages/ControlTowerPage'));
const ManagedPortalPage = lazy(() => import('@/features/tenant/pages/ManagedPortalPage'));
const LightPortalPage = lazy(() => import('@/features/tenant/pages/LightPortalPage'));

// Loading component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-96">
    <div className="flex flex-col items-center space-y-4">
      <Spinner />
      <p className="text-neutral-500 text-sm">Chargement...</p>
    </div>
  </div>
);

// Component to redirect authenticated users away from login
const LoginRoute: React.FC = () => {
  const { isAuthenticated } = useAuthStore();

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <LoginPage />;
};

export const AppRouter: React.FC = () => {
  const { isEnabled } = useFeaturesStore();

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<LoginRoute />} />
        {isEnabled('xroad') && <Route path="/public/join" element={<PublicOnboardingPage />} />}
        {isEnabled('xroad') && <Route path="/public/verify/:token" element={<VerifyEmailPage />} />}

        {/* Protected Routes */}
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Routes>
                  {/* Dashboard — always available */}
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />
                  <Route path="dashboard" element={<DashboardPage />} />

                  {/* Core modules — always available in both products */}
                  <Route path="datasources" element={<DatasourcesListPage />} />
                  <Route path="datasources/new" element={<CreateDatasourcePage />} />
                  <Route path="datasources/:id" element={<DatasourceDetailPage />} />
                  <Route path="schema-explorer" element={<SchemaExplorerPageV2 />} />
                  <Route path="endpoints" element={<EndpointsPage />} />
                  <Route path="endpoints/new" element={<CreateEndpointPage />} />
                  <Route path="endpoints/:id" element={<EndpointDetailPage />} />
                  <Route path="query-builder" element={<QueryBuilderPage />} />

                  {/* Sync — always available */}
                  <Route path="sync" element={<SyncPage />} />
                  <Route path="sync/:jobId" element={<SyncJobDetailPage />} />
                  {isEnabled('syncTest') && <Route path="sync-test" element={<SyncTestPage />} />}

                  {/* X-Road — feature flagged */}
                  {isEnabled('xroad') && <>
                    <Route path="xroad" element={<XRoadModule />} />
                    <Route path="xroad/overview" element={<XRoadOverviewPage />} />
                    <Route path="xroad/applications" element={<ConsumerApplicationsPage />} />
                    <Route path="xroad/subscriptions" element={<SubscriptionsPage />} />
                    <Route path="xroad/services" element={<PublishedServicesPage />} />
                    <Route path="xroad/services/publish" element={<PublishServiceWizard />} />
                    <Route path="xroad/certificates" element={<CertificateMonitoringPage />} />
                    <Route path="xroad/api-keys" element={<Navigate to="/xroad/my-access" replace />} />
                    <Route path="xroad/my-access" element={<MyAccessPage />} />
                    <Route path="xroad/catalog" element={<ServiceCatalogPage />} />
                    <Route path="xroad/consume" element={<ConsumeServicePage />} />
                  </>}

                  {/* JSONata — feature flagged */}
                  {isEnabled('jsonata') && <>
                    <Route path="jsonata/playground" element={<JsonataPlaygroundPage />} />
                    <Route path="jsonata/udfs" element={<UDFManagerPage />} />
                  </>}

                  {/* DataLens AI — feature flagged */}
                  {isEnabled('datalens') && <Route path="datalens" element={<DataLensPage />} />}

                  {/* DQ Engine — feature flagged */}
                  {isEnabled('dq') && <>
                    <Route path="dq" element={<Navigate to="/dq/dashboard" replace />} />
                    <Route path="dq/:tab" element={<DqDashboardPage />} />
                  </>}

                  {/* Intelligence Interopérabilité — feature flagged */}
                  {isEnabled('governance') && <>
                    <Route path="governance" element={<Navigate to="/governance/hub" replace />} />
                    <Route path="governance/hub" element={<HubPage />} />
                    <Route path="governance/catalog" element={<CatalogPage />} />
                    <Route path="governance/lineage" element={<LineageGraphPage />} />
                    <Route path="governance/glossary" element={<GlossaryListPage />} />
                    <Route path="governance/glossary/terms/:termId" element={<TermDetailPage />} />
                    <Route path="governance/quality" element={<QualityDashboardPage />} />
                    <Route path="governance/supervision" element={<AccessSupervisionPage />} />
                    {/* Legacy redirects — Domains + DataProducts removed (SENUM rebrand) */}
                    <Route path="governance/domains" element={<Navigate to="/governance/hub" replace />} />
                    <Route path="governance/data-products" element={<Navigate to="/governance/hub" replace />} />
                  </>}

                  {/* Registry Builder */}
                  <Route path="registries" element={<RegistryListPage />} />
                  <Route path="registries/create" element={<CreateRegistryPage />} />
                  <Route path="registries/:id" element={<RegistryDetailPage />} />

                  {/* Privacy & Protection — feature flagged */}
                  {isEnabled('privacy') && <>
                    <Route path="privacy" element={<Navigate to="/privacy/registry" replace />} />
                    <Route path="privacy/registry" element={<PrivacyRegistryPage />} />
                    <Route path="privacy/cartography" element={<PrivacyCartographyPage />} />
                    <Route path="privacy/compliance" element={<PrivacyCompliancePage />} />
                  </>}

                  {/* Tenant Portals — feature flagged */}
                  {isEnabled('portals') && <>
                    <Route path="portal/operator" element={<OperatorConsolePage />} />
                    <Route path="portal/control-tower" element={<ControlTowerPage />} />
                    <Route path="portal/managed" element={<ManagedPortalPage />} />
                    <Route path="portal/light" element={<LightPortalPage />} />
                  </>}

                  {/* 404 Fallback */}
                  <Route path="*" element={<Navigate to="/dashboard" replace />} />
                </Routes>
              </AppLayout>
            </ProtectedRoute>
          }
        />
      </Routes>
    </Suspense>
  );
};
